/**
 * AdminGrantAccess — Oct 2026
 *
 * One-click "give a friend access to a specific journey". The flow
 * Michael kept running by hand (create user → set restricted_journeys
 * → mint magic link → write WhatsApp message) collapses to:
 *
 *   1. pick a journey
 *   2. type a name + email
 *   3. click Grant access
 *   4. tap Copy WhatsApp message
 *
 * Lives at /admin/grant-access (admin-only).
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AdminLayout } from "../AdminPages";
import {
  ArrowLeft, UserPlus, Loader2, Copy, Check, MessageCircle,
  KeyRound, RotateCw,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem("reclaim_token")}` });

export default function AdminGrantAccess() {
  const nav = useNavigate();
  const [journeys, setJourneys] = useState([]);
  const [loadingJourneys, setLoadingJourneys] = useState(true);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [slug, setSlug] = useState("");
  const [resetPassword, setResetPassword] = useState(true);

  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");
  const [copied, setCopied] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/api/admin/journeys-brief`, { headers: auth() });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const d = await r.json();
        setJourneys(d.items || []);
        if (!slug && (d.items || []).length) setSlug(d.items[0].slug);
      } catch (e) {
        setErr(String(e.message || e));
      } finally {
        setLoadingJourneys(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async (e) => {
    e?.preventDefault?.();
    setErr("");
    setResult(null);
    setSubmitting(true);
    try {
      const r = await fetch(`${API}/api/admin/grant-journey-access`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...auth() },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim().toLowerCase(),
          journey_slug: slug,
          reset_password: resetPassword,
        }),
      });
      if (!r.ok) {
        const t = await r.text();
        throw new Error(t || `HTTP ${r.status}`);
      }
      const d = await r.json();
      setResult(d);
    } catch (e) {
      setErr(String(e.message || e));
    } finally {
      setSubmitting(false);
    }
  };

  const copy = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      setTimeout(() => setCopied(null), 1500);
    } catch (_) { /* clipboard blocked — text still on screen */ }
  };

  const absUrl = (path) => {
    if (!path) return "";
    if (/^https?:\/\//i.test(path)) return path;
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    return `${origin}${path.startsWith("/") ? "" : "/"}${path}`;
  };

  const resetForm = () => {
    setResult(null);
    setErr("");
    setName("");
    setEmail("");
    // keep slug — admin is likely granting access to the same journey repeatedly
  };

  // ─── Build the WhatsApp message ───────────────────────────────────
  const buildWhatsAppMessage = (r) => {
    if (!r) return "";
    const first = (r.name || "").split(" ")[0] || "there";
    const magic = absUrl(r.magic_url);
    const title = r.journey_title || "the journey";
    const lines = [
      `Hey ${first} 👋`,
      "",
      `Here's your access to ${title} — a calm, state-based path. 20 minutes a day.`,
      "",
      "One-tap sign-in (works on your phone):",
      magic,
      "",
    ];
    if (r.temp_password) {
      lines.push(
        "Or sign in the usual way:",
        `  Email:    ${r.email}`,
        `  Password: ${r.temp_password}`,
        "  (You can change it under Account once you're in.)",
        "",
      );
    }
    lines.push("Any questions, message me.");
    return lines.join("\n");
  };
  const waText = buildWhatsAppMessage(result);
  const waHref = result ? `https://wa.me/?text=${encodeURIComponent(waText)}` : "#";

  return (
    <AdminLayout title="Grant journey access">
      <div className="mb-6 flex items-center justify-between" data-testid="admin-grant-access">
        <button
          type="button"
          onClick={() => nav("/admin")}
          className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900"
          data-testid="grant-access-back"
        >
          <ArrowLeft className="w-4 h-4" /> Back to admin
        </button>
      </div>

      <div className="mb-6 rounded-xl border border-slate-200 bg-white p-5">
        <div className="flex items-start gap-3">
          <div className="rounded-full bg-emerald-50 text-emerald-700 p-2 flex-shrink-0">
            <UserPlus className="w-5 h-5" />
          </div>
          <div>
            <h2 className="font-semibold text-slate-900">Give a friend access to one journey</h2>
            <p className="text-sm text-slate-500 mt-1">
              Creates (or updates) their account, locks them to the chosen
              journey only, mints a one-tap magic sign-in link, and gives you a
              ready-to-paste WhatsApp message. Takes about 10 seconds.
            </p>
          </div>
        </div>
      </div>

      {!result ? (
        <form
          onSubmit={submit}
          className="space-y-4 bg-white rounded-2xl border border-slate-200 p-5"
          data-testid="grant-access-form"
        >
          <div>
            <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1.5">
              Journey
            </label>
            {loadingJourneys ? (
              <div className="flex items-center gap-2 text-sm text-slate-500 py-2">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading journeys…
              </div>
            ) : (
              <select
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm bg-white"
                data-testid="grant-access-journey"
                required
              >
                <option value="" disabled>Choose a journey…</option>
                {journeys.map((j) => (
                  <option key={j.slug} value={j.slug}>
                    {j.title}{j.total_days ? ` · ${j.total_days} days` : ""}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1.5">
                Their name
              </label>
              <input
                type="text"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Santah"
                className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm"
                data-testid="grant-access-name"
                autoComplete="off"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1.5">
                Their email
              </label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="friend@example.com"
                className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm"
                data-testid="grant-access-email"
                autoComplete="off"
                autoCapitalize="off"
              />
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm text-slate-600 pt-1">
            <input
              type="checkbox"
              checked={resetPassword}
              onChange={(e) => setResetPassword(e.target.checked)}
              data-testid="grant-access-reset-pwd"
            />
            Reset the password (uncheck only if they already know it)
          </label>

          {err && (
            <div className="text-sm text-red-600" data-testid="grant-access-error">{err}</div>
          )}

          <button
            type="submit"
            disabled={submitting || !slug || !name.trim() || !email.trim()}
            className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-slate-900 text-white px-4 py-3 text-sm disabled:opacity-50"
            data-testid="grant-access-submit"
          >
            {submitting
              ? <><Loader2 className="w-4 h-4 animate-spin" /> Granting access…</>
              : <><KeyRound className="w-4 h-4" /> Grant access</>}
          </button>
        </form>
      ) : (
        <ResultCard
          result={result}
          absUrl={absUrl}
          waText={waText}
          waHref={waHref}
          copied={copied}
          onCopy={copy}
          onReset={resetForm}
        />
      )}
    </AdminLayout>
  );
}

function ResultCard({ result, absUrl, waText, waHref, copied, onCopy, onReset }) {
  const magic = absUrl(result.magic_url);
  return (
    <div className="space-y-4" data-testid="grant-access-result">
      <div
        className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5"
      >
        <div className="flex items-start gap-3">
          <div className="rounded-full bg-emerald-600 text-white p-1.5 flex-shrink-0">
            <Check className="w-4 h-4" />
          </div>
          <div>
            <h3 className="font-semibold text-emerald-900">
              {result.created ? "Account created" : "Access updated"}
            </h3>
            <p className="text-sm text-emerald-800 mt-0.5">
              {result.name} ({result.email}) is now locked to{" "}
              <strong>{result.journey_title}</strong>.
            </p>
          </div>
        </div>
      </div>

      {/* The access bundle */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5 space-y-4">
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-500 mb-1.5">
            One-tap magic sign-in
          </div>
          <div className="flex items-center gap-2 rounded-lg bg-slate-50 border border-slate-200 p-2 text-xs font-mono text-slate-700 overflow-hidden">
            <span className="truncate flex-1" title={magic}>{magic}</span>
            <button
              type="button"
              onClick={() => onCopy(magic, "magic")}
              className="flex-shrink-0 inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-2 py-1 text-slate-600 hover:text-slate-900"
              data-testid="grant-access-copy-link"
            >
              {copied === "magic"
                ? <Check className="w-3.5 h-3.5 text-emerald-500" />
                : <Copy className="w-3.5 h-3.5" />}
              Copy
            </button>
          </div>
        </div>

        {result.temp_password && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-500 mb-1.5">
                Email
              </div>
              <div className="flex items-center gap-2 rounded-lg bg-slate-50 border border-slate-200 p-2 text-sm font-mono text-slate-800">
                <span className="truncate flex-1">{result.email}</span>
                <button
                  type="button"
                  onClick={() => onCopy(result.email, "email")}
                  className="flex-shrink-0 text-slate-500 hover:text-slate-900"
                  data-testid="grant-access-copy-email"
                >
                  {copied === "email"
                    ? <Check className="w-3.5 h-3.5 text-emerald-500" />
                    : <Copy className="w-3.5 h-3.5" />}
                </button>
              </div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-500 mb-1.5">
                Temp password
              </div>
              <div className="flex items-center gap-2 rounded-lg bg-slate-50 border border-slate-200 p-2 text-sm font-mono text-slate-800">
                <span className="truncate flex-1">{result.temp_password}</span>
                <button
                  type="button"
                  onClick={() => onCopy(result.temp_password, "pwd")}
                  className="flex-shrink-0 text-slate-500 hover:text-slate-900"
                  data-testid="grant-access-copy-password"
                >
                  {copied === "pwd"
                    ? <Check className="w-3.5 h-3.5 text-emerald-500" />
                    : <Copy className="w-3.5 h-3.5" />}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* WhatsApp block */}
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-500 mb-1.5">
            Ready-to-paste WhatsApp message
          </div>
          <pre
            className="whitespace-pre-wrap text-sm text-slate-700 bg-slate-50 border border-slate-200 rounded-lg p-3 font-sans leading-relaxed"
            data-testid="grant-access-wa-preview"
          >
            {waText}
          </pre>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => onCopy(waText, "wa")}
              className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
              data-testid="grant-access-copy-wa"
            >
              {copied === "wa"
                ? <Check className="w-4 h-4 text-emerald-500" />
                : <Copy className="w-4 h-4" />}
              Copy WhatsApp message
            </button>
            <a
              href={waHref}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 rounded-full bg-emerald-600 text-white px-3 py-1.5 text-sm hover:bg-emerald-700"
              data-testid="grant-access-open-wa"
            >
              <MessageCircle className="w-4 h-4" />
              Open in WhatsApp
            </a>
            <button
              type="button"
              onClick={onReset}
              className="ml-auto inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900"
              data-testid="grant-access-another"
            >
              <RotateCw className="w-4 h-4" />
              Grant another
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
